import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Divider,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { SEVV_MODAL } from '../VirtualRoom/VirtualRoomModalStyles'
import { ContentHeader, PageTitle } from '../styles/MainMenu.styles'
import { useAppSelector, useAppDispatch } from '../../hooks'
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions'
import { openBookingForm, triggerBookingsRefresh } from '../../stores/RoomBookingStore'
import { addNotification } from '../../stores/NotificationStore'
import { parseCloudRun } from '../../services/parseCloud'
import type { RoomBookingData } from '../../../../types/RoomBooking'
import {
  formatDateHeader,
  formatTimeRange,
  getStatusColor,
  getBookingStatusChip,
  InviteeList,
  BookingInviteeAvatar,
} from './bookingCardUtils'
import { useMyBookingLists, type MeetingsView } from './useMyBookingLists'

// parseCloudRun lets a company-closed refusal (SC-409) re-check access, so the disabled screen
// appears. Session handling stays as it was before: no sign-out here on an expired token.
const CLOUD_OPTS = { handleInvalidSession: false }

export default function MeetingsDashboardPanel() {
  const dispatch = useAppDispatch()
  const { myBookings, myInvites, bookingsRefreshTrigger } = useAppSelector((s) => s.roomBooking)
  const { currentCompany } = useCompanyPermissions()
  const companyId = currentCompany?.id || ''
  const currentUserId = useAppSelector((s) => s.user.userData?.id) || ''
  const [tab, setTab] = useState(0)
  const [cancelling, setCancelling] = useState<string | null>(null)
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null)
  const [viewFilter, setViewFilter] = useState<MeetingsView>('upcoming')
  const [visibleCount, setVisibleCount] = useState(10)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const loading = useMyBookingLists(companyId, true, viewFilter, bookingsRefreshTrigger)

  const handleCancelConfirmed = async () => {
    if (!confirmCancelId) return
    const bookingId = confirmCancelId
    setConfirmCancelId(null)
    setCancelling(bookingId)
    try {
      await parseCloudRun('cancelRoomBooking', { bookingId }, CLOUD_OPTS)
      dispatch(addNotification({ type: 'success', message: 'Meeting cancelled', duration: 4000 }))
      // Reloads this panel (its effect watches the trigger) and today's bookings (App.tsx)
      dispatch(triggerBookingsRefresh())
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', message: err.message || 'Failed to cancel', duration: 5000 }))
    } finally {
      setCancelling(null)
    }
  }

  const handleEdit = (booking: RoomBookingData) => {
    dispatch(openBookingForm({ editingId: booking.objectId, companyId }))
  }

  const handleRsvp = async (bookingId: string, action: 'accepted' | 'declined' | 'maybe') => {
    try {
      await parseCloudRun('respondToBookingInvite', { bookingId, action }, CLOUD_OPTS)
      dispatch(
        addNotification({
          type: 'success',
          message: action === 'accepted' ? 'Invite accepted' : action === 'declined' ? 'Invite declined' : 'Responded maybe',
          duration: 4000,
        }),
      )
      // Also re-reads the reminders' list, so a declined meeting gets no reminder
      dispatch(triggerBookingsRefresh())
    } catch (err: any) {
      dispatch(addNotification({ type: 'error', message: err.message || 'Failed to respond', duration: 5000 }))
    }
  }

  const allBookings = myBookings
  const allInvites = myInvites.filter((b) =>
    b.invitees.some((inv) => inv.userId === currentUserId && (inv.status === 'pending' || inv.status === 'declined' || inv.status === 'maybe')),
  )

  const renderBookingCard = (booking: RoomBookingData, isOwner: boolean) => {
    const now = new Date()
    const isPast = new Date(booking.endTime) < now
    const isActive = new Date(booking.startTime) <= now && !isPast
    const isCancelled = booking.status === 'CANCELLED'
    const myInvite = booking.invitees.find((inv) => inv.userId === currentUserId) || null

    return (
      <Box
        key={booking.objectId}
        sx={{
          p: 2,
          borderRadius: '8px',
          border: '1px solid var(--sevv-divider, rgba(255,255,255,0.1))',
          opacity: isPast || isCancelled ? 0.6 : 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(booking), mt: 0.75, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {booking.title}
              </Typography>
              {getBookingStatusChip(booking)}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {formatTimeRange(booking.startTime, booking.endTime)}
              {booking.roomName && ` · ${booking.roomName}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
              {(isOwner || booking.creatorName) ? (
                <Typography variant="caption" color="text.secondary">
                  Organised by {isOwner ? 'you' : booking.creatorName}
                </Typography>
              ) : <Box />}
              {booking.invitees.length > 0 && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === booking.objectId ? null : booking.objectId!) }}
                >
                  {booking.invitees.slice(0, 4).map((inv, i) => (
                    <Tooltip key={inv.userId || i} title={inv.name || inv.email || 'Unknown'}>
                      <BookingInviteeAvatar invitee={inv} />
                    </Tooltip>
                  ))}
                  {booking.invitees.length > 4 && (
                    <Typography variant="caption" color="text.secondary">+{booking.invitees.length - 4}</Typography>
                  )}
                </Box>
              )}
            </Box>
            {myInvite && myInvite.status !== 'pending' && (
              <Chip
                label={myInvite.status === 'accepted' ? 'Accepted' : myInvite.status === 'declined' ? 'Declined' : 'Maybe'}
                size="small"
                color={myInvite.status === 'accepted' ? 'success' : myInvite.status === 'declined' ? 'error' : 'warning'}
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
            {expandedId === booking.objectId && booking.invitees.length > 0 && (
              <InviteeList invitees={booking.invitees} currentUserId={currentUserId} />
            )}

            {!isPast && !isCancelled && !isActive && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                {isOwner ? (
                  <>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(booking)} sx={SEVV_MODAL.btnSecondary}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={cancelling === booking.objectId ? <CircularProgress size={14} /> : <CancelIcon />}
                      onClick={() => setConfirmCancelId(booking.objectId!)}
                      disabled={cancelling === booking.objectId}
                      sx={SEVV_MODAL.btnDanger}
                    >
                      Cancel
                    </Button>
                  </>
                ) : myInvite ? (
                  <>
                    {myInvite.status !== 'accepted' && (
                      <Button size="small" onClick={() => handleRsvp(booking.objectId!, 'accepted')} sx={SEVV_MODAL.btnPrimary}>
                        Accept
                      </Button>
                    )}
                    {myInvite.status !== 'maybe' && (
                      <Button size="small" onClick={() => handleRsvp(booking.objectId!, 'maybe')} sx={SEVV_MODAL.btnSecondary}>
                        Maybe
                      </Button>
                    )}
                    {myInvite.status !== 'declined' && (
                      <Button size="small" onClick={() => handleRsvp(booking.objectId!, 'declined')} sx={SEVV_MODAL.btnSecondary}>
                        Decline
                      </Button>
                    )}
                  </>
                ) : null}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  const renderBookingList = (bookings: RoomBookingData[], isOwnerTab: boolean) => {
    if (bookings.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {isOwnerTab
            ? viewFilter === 'past' ? 'No past meetings' : viewFilter === 'today' ? 'No meetings today' : 'No upcoming meetings'
            : 'No invitations'}
          .
        </Typography>
      )
    }

    const items: React.ReactNode[] = []
    let lastDateKey = ''
    bookings.slice(0, visibleCount).forEach((b) => {
      const dateKey = new Date(b.startTime).toDateString()
      if (dateKey !== lastDateKey) {
        lastDateKey = dateKey
        items.push(
          <Typography key={`header-${dateKey}`} variant="caption" fontWeight={700} color="text.secondary"
            sx={{ display: 'block', mt: items.length ? 2 : 0, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
            {formatDateHeader(b.startTime)}
          </Typography>
        )
      }
      items.push(renderBookingCard(b, isOwnerTab ? b.creatorId === currentUserId : false))
    })

    return (
      <>
        {items}
        {visibleCount < bookings.length && (
          <Button fullWidth onClick={() => setVisibleCount((v) => v + 10)} sx={{ ...SEVV_MODAL.btnSecondary, mt: 1 }}>
            Show More ({bookings.length - visibleCount} remaining)
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      <ContentHeader>
        <PageTitle>Meetings</PageTitle>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => dispatch(openBookingForm({ companyId }))}
          size="small"
          sx={SEVV_MODAL.btnPrimary}
        >
          Schedule Meeting
        </Button>
      </ContentHeader>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Tabs value={tab} onChange={(_e, v) => { setTab(v); setVisibleCount(10) }} sx={{ minHeight: 40 }}>
          <Tab label={`My Meetings (${allBookings.length})`} sx={{ textTransform: 'none', minHeight: 40 }} />
          <Tab label={`Invitations (${allInvites.length})`} sx={{ textTransform: 'none', minHeight: 40 }} />
        </Tabs>
        {tab === 0 && (
          <ToggleButtonGroup
            value={viewFilter}
            exclusive
            size="small"
            onChange={(_e, v) => { if (v) { setViewFilter(v); setVisibleCount(10) } }}
            sx={{ height: 28 }}
          >
            <ToggleButton value="upcoming" sx={{ textTransform: 'none', px: 1.5, fontSize: 12 }}>Upcoming</ToggleButton>
            <ToggleButton value="today" sx={{ textTransform: 'none', px: 1.5, fontSize: 12 }}>Today</ToggleButton>
            <ToggleButton value="past" sx={{ textTransform: 'none', px: 1.5, fontSize: 12 }}>Past</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : tab === 0 ? (
        renderBookingList(allBookings, true)
      ) : (
        renderBookingList(allInvites, false)
      )}

      <Dialog
        open={!!confirmCancelId}
        onClose={() => setConfirmCancelId(null)}
        PaperProps={{ sx: { ...SEVV_MODAL.paper, minWidth: 360 } }}
        slotProps={{ backdrop: { sx: SEVV_MODAL.backdrop } }}
      >
        <DialogTitle sx={SEVV_MODAL.title}>Cancel Meeting</DialogTitle>
        <DialogContent sx={SEVV_MODAL.content}>
          <Typography variant="body2">
            Are you sure you want to cancel this meeting? All invitees will be notified.
          </Typography>
        </DialogContent>
        <DialogActions sx={SEVV_MODAL.actions}>
          <Button onClick={() => setConfirmCancelId(null)} sx={SEVV_MODAL.btnSecondary} size="small">
            No, Keep It
          </Button>
          <Button onClick={handleCancelConfirmed} sx={SEVV_MODAL.btnDanger} size="small">
            Yes, Cancel Meeting
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
